/**
 * Condução da partida: fases, passagem de turno e condição de vitória.
 *
 * Uma fase é a vez de uma equipe. Ao começar, as unidades dela recuperam a ação
 * e sofrem o que o tabuleiro tiver a cobrar — veneno, queimadura, cura de
 * forte. Ao terminar, a vez passa; quando volta à primeira equipe da ordem, o
 * turno avança.
 */
import { terrainAt } from './grid.js';
import { beginTurn, endTurn, hasEffect, upkeepDamage } from './states.js';
import { livingUnits } from './state.js';
import { covers } from './types.js';
/** A equipe que joga depois desta, em ciclo. */
export function nextTeam(state) {
    const index = state.turnOrder.indexOf(state.phase);
    return state.turnOrder[(index + 1) % state.turnOrder.length] ?? state.turnOrder[0];
}
/**
 * Efeitos que o começo da fase cobra de cada unidade da equipe.
 *
 * Veneno e queimadura podem ferir, mas nunca matam: uma unidade que morre
 * sozinha, sem ninguém atacar, é a forma mais frustrante de perder um
 * personagem. É a mesma decisão da série. Depois do dano, cada condição perde
 * um turno — e continua valendo até o fim desta fase (ver `states.ts`).
 */
function applyUpkeep(units, state, content, team) {
    const events = [];
    const next = units.map((unit) => {
        if (unit.team !== team || !unit.alive)
            return unit;
        const terrain = terrainAt(state.map, content, unit.position);
        const terrainHeal = terrain?.heal
            ? Math.max(1, Math.floor((unit.stats.hp * terrain.heal) / 100))
            : 0;
        let hp = unit.currentHp;
        for (const hit of upkeepDamage(unit.states, unit.stats.hp)) {
            const before = hp;
            hp = Math.max(1, hp - hit.amount);
            if (hp < before) {
                events.push({ type: 'condicao_efeito', unitId: unit.id, condition: hit.condition, amount: before - hp });
            }
        }
        hp = Math.min(unit.stats.hp, hp + terrainHeal);
        return {
            ...unit,
            currentHp: hp,
            hasActed: false,
            ...(unit.states.length > 0 ? { states: beginTurn(unit.states) } : {}),
        };
    });
    return { units: next, events };
}
/**
 * O fim da fase de uma equipe: sai a condição que já cobrou o último turno.
 *
 * É aqui, e não no começo da fase seguinte, que ela sai — senão a paralisia de
 * um turno sairia antes de tirar a jogada que ela existe para tirar.
 */
function expireConditions(units, team) {
    const events = [];
    const next = units.map((unit) => {
        if (unit.team !== team || !unit.alive || unit.states.length === 0)
            return unit;
        const { kept, expired } = endTurn(unit.states);
        if (expired.length === 0)
            return unit;
        for (const condition of expired) {
            events.push({ type: 'condicao_removida', unitId: unit.id, condition, reason: 'terminou' });
        }
        return { ...unit, states: kept };
    });
    return { units: next, events };
}
/** Encerra a fase atual e prepara a seguinte. */
export function endPhase(state, content) {
    if (state.status !== 'em_andamento')
        return { state, events: [] };
    const finished = state.phase;
    const upcoming = nextTeam(state);
    const wrapped = state.turnOrder.indexOf(upcoming) === 0;
    const expiry = expireConditions(state.units, finished);
    const upkeep = applyUpkeep(expiry.units, state, content, upcoming);
    const advanced = {
        ...state,
        phase: upcoming,
        turn: wrapped ? state.turn + 1 : state.turn,
        units: upkeep.units,
    };
    const settled = { ...advanced, status: evaluateObjective(advanced) };
    return {
        state: settled,
        events: [{ type: 'fase_encerrada', team: finished }, ...expiry.events, ...upkeep.events],
    };
}
/** Todas as unidades da equipe já agiram (ou não podem agir)? */
export function phaseIsOver(state) {
    return livingUnits(state, state.phase).every((u) => u.hasActed || hasEffect(u.states, 'nao_age'));
}
/**
 * Situação da partida.
 *
 * A derrota é avaliada antes da vitória: perder o comandante enquanto se cumpre
 * o objetivo é derrota, e não empate. Numa disputa é a regra menos surpreendente.
 */
export function evaluateObjective(state) {
    const players = livingUnits(state, 'jogador');
    const enemies = livingUnits(state, 'inimigo');
    if (players.length === 0)
        return 'derrota';
    const playerCommander = players.find((u) => u.isCommander);
    const hasCommanderRole = state.units.some((u) => u.team === 'jogador' && u.isCommander);
    if (hasCommanderRole && !playerCommander)
        return 'derrota';
    // Se Distortidus (o lorde protagonista) morreu no time do jogador, derrota imediata
    const lordFallen = state.units.some((u) => u.team === 'jogador' && u.name === 'Distortidus' && !u.alive);
    if (lordFallen)
        return 'derrota';
    switch (state.objective.kind) {
        case 'derrotar_todos':
            return enemies.length === 0 ? 'vitoria' : 'em_andamento';
        case 'derrotar_comandante': {
            const enemyCommanderAlive = enemies.some((u) => u.isCommander);
            const hadEnemyCommander = state.units.some((u) => u.team === 'inimigo' && u.isCommander);
            if (!hadEnemyCommander)
                return enemies.length === 0 ? 'vitoria' : 'em_andamento';
            return enemyCommanderAlive ? 'em_andamento' : 'vitoria';
        }
        case 'sobreviver':
            return state.turn > state.objective.turns ? 'vitoria' : 'em_andamento';
        case 'tomar_posicao': {
            const at = state.objective.at;
            // O corpo inteiro toma a posição: um dragão com a pata no trono está no
            // trono, mesmo que a âncora dele esteja na casa ao lado.
            const held = players.some((u) => covers(u.position, u.size, at));
            return held ? 'vitoria' : 'em_andamento';
        }
    }
}
/** Reavalia a situação depois de uma ação. */
export function settle(state) {
    if (state.status !== 'em_andamento')
        return state;
    return { ...state, status: evaluateObjective(state) };
}
//# sourceMappingURL=match.js.map